import { TFunction } from 'i18next';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { formatProperty } from '../../utilFunctions/formatters';
import { getAliasForDataField } from '../api/alias/utils';
import { DataValue } from '../api/types';
import { ASCII_ARROW, Languages, UNDEFINED_DISPLAY_KEY } from '../constants';
import { useAliasesContext, useGlobalLocaleContext } from '../context/contexts';
import { DataFieldWithDataType, SegmentBy, ValueTypes } from '../types';
import { getUndefinedValue } from '../utils';
import { ManualDataRefetchCountHandle } from './types';
import { translateLabel } from './utils';

export const useManualRefetchCountHandle = (): ManualDataRefetchCountHandle => {
  const [manualDataRefetchCount, setManualDataRefetchCount] = useState(0);
  return {
    current: manualDataRefetchCount,
    increment: useCallback(
      () => setManualDataRefetchCount((prev) => prev + 1),
      []
    ),
  };
};

export const useStateWithLocalStorage = <T>(
  initialValue: T,
  key: string
): [T, Dispatch<SetStateAction<T>>] => {
  const [value, setValue] = useState<T>(() => {
    const storedValue = localStorage.getItem(key);
    if (storedValue) {
      try {
        return JSON.parse(storedValue);
      } catch {
        return initialValue;
      }
    }
    return initialValue;
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
};

/**
 * Extracts the segment value from a group segment value.
 * For employee-cohort segment types, extracts the first value from the object.
 * For normal filter types, returns the value directly.
 */
export const extractSegmentValue = (
  value: DataValue,
  segmentBy: SegmentBy
): DataValue => {
  if (segmentBy.type === 'employee-cohort') {
    // For employee-cohort types, value is an object, extract the first value
    return value && typeof value === 'object'
      ? (Object.values(value)[0] ?? null)
      : null;
  } else {
    return value;
  }
};

/**
 * Extracts the segment value and formats it using the provided formatter.
 * For employee-cohort segment types, extracts the first value from the object
 * and uses the underlying dataField (not COHORT_VALUE) for formatting.
 * For normal filter types, returns the value directly and uses the segmentBy's dataField.
 */
export const extractAndFormatSegmentValue = (
  value: DataValue,
  segmentBy: SegmentBy,
  formatter: DataFieldValueFormatter
): string => {
  const extractedValue = extractSegmentValue(value, segmentBy);
  // For all segment types, including employee-cohort, segmentBy.dataField
  // contains the correct underlying field to use for formatting
  return formatter([extractedValue], segmentBy.dataField);
};

export const formatHierarchicalLabel = (
  label: DataValue[],
  dataField: DataFieldWithDataType,
  defaultValue: string,
  t: TFunction
): string => {
  return label
    .map((v: DataValue) => {
      const translatedLabel = translateLabel(v as ValueTypes, dataField);
      return translatedLabel
        ? (t(translatedLabel, { defaultValue: UNDEFINED_DISPLAY_KEY }) ??
            defaultValue)
        : v === null || v === undefined
          ? defaultValue
          : v;
    })
    .join(ASCII_ARROW);
};

export type DataFieldValueFormatter = (
  value: DataValue[],
  dataField: DataFieldWithDataType
) => string;

export const useDataFieldValueFormatter = () => {
  const { t } = useTranslation();
  return (value: DataValue[], dataField: DataFieldWithDataType) => {
    return formatHierarchicalLabel(value, dataField, getUndefinedValue(t), t);
  };
};

/**
 * Formats a field's display label: alias first, then the live i18n translation, then a
 * beautified fallback of the raw field name. Plain function (not a hook) so it can be
 * called from outside components too — e.g. sort comparators, CSV export, non-React
 * services — anywhere a `t`/aliases/locale triple is already in scope.
 *
 * This is the one place this alias → translation → beautify chain should live; other
 * call sites that reimplement it (e.g. {@link formatApiMasterDataProperty} and its
 * ~15 callers, which resolve aliases and translations separately from the caller) are
 * candidates to migrate onto this instead of growing further copies.
 */
export const formatFieldLabel = (
  field: DataFieldWithDataType,
  aliases: Parameters<typeof getAliasForDataField>[0],
  locale: Languages,
  t: TFunction
): string => {
  const alias = getAliasForDataField(aliases, locale)(field);
  if (alias) return alias;
  const labelKey = `common:api.masterDataField.${field.dataField.toCamel()}`;
  const translated = t(labelKey, { defaultValue: '' });
  // Fields without a translation (custom/HR-specific fields) fall back to their raw
  // SCREAMING_SNAKE_CASE key elsewhere unless beautified — match the column picker's
  // "Youngest Child Birthrate" formatting instead of a literal "YOUNGEST_CHILD_BIRTHRATE".
  return translated || formatProperty(field.dataField, '_');
};

/**
 * Hook that returns a function to format field labels with alias fallback.
 * Returns the alias if available, otherwise falls back to the translated field name.
 *
 * @returns A function that takes a DataFieldWithDataType and returns the formatted label string
 *
 * @example
 * ```tsx
 * const formatFieldLabel = useFieldLabelFormatter();
 * const label = formatFieldLabel(field);
 * ```
 */
export const useFieldLabelFormatter = () => {
  const aliases = useAliasesContext();
  const locale = useGlobalLocaleContext();
  const { t } = useTranslation();

  return (field: DataFieldWithDataType): string =>
    formatFieldLabel(field, aliases, locale.selected, t);
};
